import * as THREE from 'three';

export function createWorldInteractSystem({
  camera,
  ui,
  mobSystem,
  heldItem,
  sounds,
  isCreativeMode,
  openVillagerTrade,
  openInventory,
  openFurnace,
  openChest,
  toggleInteractiveBlock,
  tryUseBed,
  tryIgnitePortalFromHitFace,
  tryIgnitePortalFromObsidianBlock,
  tryIgniteFireFromHitFace,
  tryActivateEndPortalFromQuartzBlock,
  tryToggleLever,
  tryUseFarmingOnBlock,
  obsidianBlockId,
  quartzBlockId
} = {}) {
  const attackOrigin = new THREE.Vector3();
  const attackDir = new THREE.Vector3();

  function getVillagerInteractRangeFromHit(hit) {
    return hit
      ? Math.max(0.4, Math.min(4.5, hit.distance - 0.05))
      : 4.5;
  }

  function tryOpenVillagerTradeByAim(maxRange = 4.5) {
    camera.getWorldPosition(attackOrigin);
    camera.getWorldDirection(attackDir);
    const result = mobSystem.tryInteractVillager(attackOrigin, attackDir, maxRange);
    if (!result?.hit || !result.villager) return false;
    return openVillagerTrade(result.villager);
  }

  function tryFeedPassiveMobByAim(maxRange = 4.5) {
    const activeItemId = ui.getActiveItemId();
    if (!activeItemId || ui.getActiveItemCount() <= 0) return false;

    camera.getWorldPosition(attackOrigin);
    camera.getWorldDirection(attackDir);
    const result = mobSystem.tryFeedPassiveMob(attackOrigin, attackDir, activeItemId, {
      maxDistance: maxRange,
      consumeItem: () => {
        if (isCreativeMode()) return true;
        return ui.consumeActiveItem(1);
      }
    });
    if (!result?.fed) return false;

    sounds.eat?.();
    heldItem.triggerSwing(result.babyBorn ? 0.72 : 0.46);
    return true;
  }

  function tryOpenCraftingTableByHit(hit) {
    if (!hit || hit.object.userData.id !== 11) return false;
    openInventory('table');
    return true;
  }

  function tryOpenFurnaceByHit(hit) {
    if (!hit || hit.object.userData.id !== 13) return false;
    openFurnace(hit.object);
    return true;
  }

  function tryOpenChestByHit(hit) {
    if (!hit || hit.object.userData.id !== 14) return false;
    openChest(hit.object);
    return true;
  }

  function tryToggleInteractiveBlockByHit(hit) {
    if (!hit) return false;
    const id = hit.object.userData.id;
    if (id !== 16 && id !== 17 && id !== 18 && id !== 19) return false;
    toggleInteractiveBlock(hit.object);
    return true;
  }

  function tryToggleLeverByHit(hit) {
    if (!hit || !tryToggleLever?.(hit.object)) return false;
    sounds.placeByBlockId(58);
    return true;
  }

  function tryUseBedByHit(hit) {
    if (!hit || hit.object.userData.id !== 15) return false;
    return tryUseBed(hit.object);
  }

  function tryIgnitePortalFromHitFaceByHit(hit) {
    if (!hit) return false;
    return !!tryIgnitePortalFromHitFace(hit);
  }

  function tryIgnitePortalFromObsidianByHit(hit) {
    if (!hit || hit.object.userData.id !== obsidianBlockId) return false;
    return !!tryIgnitePortalFromObsidianBlock(hit.object);
  }

  function tryIgniteFireFromHitFaceByHit(hit) {
    if (!hit) return false;
    return !!tryIgniteFireFromHitFace(hit);
  }

  function tryActivateEndPortalFromQuartzByHit(hit) {
    if (!hit || hit.object.userData.id !== quartzBlockId) return false;
    return !!tryActivateEndPortalFromQuartzBlock(hit.object);
  }

  function tryUseFarmingOnHit(hit) {
    if (!hit || !tryUseFarmingOnBlock?.(hit.object)) return false;
    sounds.placeByBlockId(hit.object.userData.id);
    return true;
  }

  return {
    getVillagerInteractRangeFromHit,
    tryOpenVillagerTradeByAim,
    tryFeedPassiveMobByAim,
    tryOpenCraftingTableByHit,
    tryOpenFurnaceByHit,
    tryOpenChestByHit,
    tryToggleInteractiveBlockByHit,
    tryToggleLeverByHit,
    tryUseBedByHit,
    tryIgnitePortalFromHitFace: tryIgnitePortalFromHitFaceByHit,
    tryIgnitePortalFromObsidianBlock: tryIgnitePortalFromObsidianByHit,
    tryIgniteFireFromHitFace: tryIgniteFireFromHitFaceByHit,
    tryActivateEndPortalFromQuartzBlock: tryActivateEndPortalFromQuartzByHit,
    tryUseFarmingOnHit
  };
}
